/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Catalog & Multi-Provider Audition Store (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { resolveWorkspaceMediaUrl } from '../lib/urls';

export type VoiceProvider = 'openai' | 'elevenlabs' | 'google' | 'azure' | 'custom_clone';
export type VoiceGender = 'female' | 'male' | 'neutral';
export type VoiceAgeGroup = 'youth' | 'adult' | 'senior';
export type VoiceToneStyle =
  | 'warm'
  | 'energetic'
  | 'authoritative'
  | 'conversational'
  | 'news'
  | 'storytelling'
  | 'empathetic';

export interface VoiceItem {
  id: string;
  name: string;
  provider: VoiceProvider;
  gender: VoiceGender;
  language: string; // e.g. 'en-US', 'vi-VN', 'ja-JP'
  languageLabel: string;
  accentRegion: string;
  ageGroup: VoiceAgeGroup;
  styleCategory: VoiceToneStyle;
  description: string;
  sampleScript: string;
  previewSampleUrl: string;
  creditRatePer100Chars: number;
  isCloned: boolean;
  isFavorite: boolean;
  badge?: string;
  modelIdentifier?: string;
}

export interface CreateClonedVoiceParams {
  name: string;
  language: string;
  gender: VoiceGender;
  description: string;
  sampleScript?: string;
  accentRegion?: string;
}

export interface VoiceCatalogState {
  voices: VoiceItem[];
  activeAuditionVoiceId: string | null;
  isPlaying: boolean;
  playbackProgress: number; // 0 to 100
  playbackSpeed: number; // 0.8 to 1.5

  // Filters
  activeTab: 'all' | 'cloned' | 'favorites';
  searchQuery: string;
  providerFilter: 'all' | VoiceProvider;
  languageFilter: string; // 'all' or 'vi-VN', 'en-US', etc.
  genderFilter: 'all' | VoiceGender;
  styleFilter: 'all' | VoiceToneStyle;

  // Actions
  setActiveTab: (tab: 'all' | 'cloned' | 'favorites') => void;
  setSearchQuery: (query: string) => void;
  setProviderFilter: (provider: 'all' | VoiceProvider) => void;
  setLanguageFilter: (lang: string) => void;
  setGenderFilter: (gender: 'all' | VoiceGender) => void;
  setStyleFilter: (style: 'all' | VoiceToneStyle) => void;
  resetFilters: () => void;

  toggleFavorite: (voiceId: string) => void;
  playAudition: (voiceId: string) => void;
  pauseAudition: () => void;
  stopAudition: () => void;
  setPlaybackSpeed: (speed: number) => void;
  setPlaybackProgress: (progress: number) => void;

  createClonedVoice: (params: CreateClonedVoiceParams) => VoiceItem;
  deleteClonedVoice: (id: string) => void;

  // Selectors
  getFilteredVoices: () => VoiceItem[];
  getActiveAuditionVoice: () => VoiceItem | undefined;
  getVoiceStats: () => {
    totalVoices: number;
    providerCount: number;
    clonedCount: number;
    favoritesCount: number;
  };
}

export const SEED_VOICES: VoiceItem[] = [
  // --- OpenAI (9 voices) ---
  {
    id: 'openai-alloy',
    name: 'Alloy',
    provider: 'openai',
    gender: 'neutral',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Neutral',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Versatile, balanced, and articulate for assistants and explainer videos.',
    sampleScript: 'Welcome to Sophia Platform. All AI media workloads execute directly on Cloudflare edge.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-alloy.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Popular',
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-echo',
    name: 'Echo',
    provider: 'openai',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Warm',
    ageGroup: 'adult',
    styleCategory: 'storytelling',
    description: 'Deep, resonant, and calm voice ideal for audiobooks and guided tutorials.',
    sampleScript: 'Step into the future of decentralized computing where low latency meets unlimited scale.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-echo.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-fable',
    name: 'Fable',
    provider: 'openai',
    gender: 'male',
    language: 'en-GB',
    languageLabel: 'English (UK)',
    accentRegion: 'British Expressive',
    ageGroup: 'adult',
    styleCategory: 'storytelling',
    description: 'Cultured, dramatic British delivery suitable for gaming and narrative storytelling.',
    sampleScript: 'Beyond the horizon of conventional architectures lies the edge computing frontier.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-fable.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-onyx',
    name: 'Onyx',
    provider: 'openai',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Deep',
    ageGroup: 'senior',
    styleCategory: 'authoritative',
    description: 'Strong, authoritative, and commanding presence for executive corporate presentations.',
    sampleScript: 'Enterprise security is deeply rooted in cryptographic verification and atomic ledger transactions.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-onyx.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-nova',
    name: 'Nova',
    provider: 'openai',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Warm',
    ageGroup: 'youth',
    styleCategory: 'energetic',
    description: 'Bright, lively, and optimistic tone favored for podcasts, product demos, and commercials.',
    sampleScript: 'Get ready to streamline your creative pipeline with instantaneous AI generation!',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-nova.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Trending',
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-shimmer',
    name: 'Shimmer',
    provider: 'openai',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Clear',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Silky smooth, empathetic voice ideal for wellness and narrative meditation.',
    sampleScript: 'Take a deep breath and let our intelligent agents take care of the heavy lifting.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-shimmer.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'tts-1',
  },
  {
    id: 'openai-ash',
    name: 'Ash',
    provider: 'openai',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Casual',
    ageGroup: 'youth',
    styleCategory: 'conversational',
    description: 'Relaxed, modern, and friendly tone for everyday chat interactions.',
    sampleScript: 'Hey there! Let me walk you through the new AI video rendering features today.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-ash.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'New',
    modelIdentifier: 'gpt-4o-mini-tts',
  },
  {
    id: 'openai-sage',
    name: 'Sage',
    provider: 'openai',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Mindful',
    ageGroup: 'adult',
    styleCategory: 'empathetic',
    description: 'Thoughtful, calm, and grounded voice for therapy, education, and mindful applications.',
    sampleScript: 'Clarity and precision guide every architectural decision we build together.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-sage.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'New',
    modelIdentifier: 'gpt-4o-mini-tts',
  },
  {
    id: 'openai-coral',
    name: 'Coral',
    provider: 'openai',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Corporate',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Polished, professional, and engaging voice tailored for customer service and SaaS onboarding.',
    sampleScript: 'Welcome aboard! Your workspace has been provisioned with full multi-model capability.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/openai-coral.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'New',
    modelIdentifier: 'gpt-4o-mini-tts',
  },

  // --- ElevenLabs (14 voices) ---
  {
    id: 'eleven-rachel',
    name: 'Rachel',
    provider: 'elevenlabs',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Professional',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Classic calm, natural narrator voice widely recognized for professional documentaries.',
    sampleScript: 'High performance edge networks deliver content at the speed of light to every continent.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-rachel.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: true,
    badge: 'Top Pick',
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-adam',
    name: 'Adam',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Deep',
    ageGroup: 'adult',
    styleCategory: 'authoritative',
    description: 'Deep, rich, and confident delivery preferred for movie trailers and high-end commercials.',
    sampleScript: 'The edge is not just a destination; it is the foundation of the next digital era.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: true,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-doni',
    name: 'Doni',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Urban',
    ageGroup: 'youth',
    styleCategory: 'energetic',
    description: 'Fast-paced, vibrant, and enthusiastic personality for social ads and gaming commentary.',
    sampleScript: 'Unleash ultra-crisp audio synthesis with instantaneous browser-side mixing!',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-doni.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-antoni',
    name: 'Antoni',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Refined',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Warm, modulated, and well-rounded tone suitable for corporate training and explainer audio.',
    sampleScript: 'Our smart audio ducking dynamically manages gains for broadcast-level harmony.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-antoni.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-bella',
    name: 'Bella',
    provider: 'elevenlabs',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Gentle',
    ageGroup: 'youth',
    styleCategory: 'storytelling',
    description: 'Gentle, expressive, and nuanced voice for children audiobooks and fantasy stories.',
    sampleScript: 'Once upon a time, distributed servers spanned across three hundred cities around the globe.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-bella.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-josh',
    name: 'Josh',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Casual',
    ageGroup: 'youth',
    styleCategory: 'conversational',
    description: 'Natural, relatable, and easy-going tone for casual podcasts and YouTube voiceovers.',
    sampleScript: 'Checking out the latest AI speech update, and honestly, the latency is incredible.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-josh.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-elli',
    name: 'Elli',
    provider: 'elevenlabs',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Vibrant',
    ageGroup: 'youth',
    styleCategory: 'energetic',
    description: 'Youthful, emotional, and lively voice great for lifestyle content and character dialogues.',
    sampleScript: 'Turn your written scripts into cinematic multi-speaker conversations in seconds!',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-elli.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-arnold',
    name: 'Arnold',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Crisp',
    ageGroup: 'senior',
    styleCategory: 'news',
    description: 'Crisp, disciplined, and measured voice appropriate for technical briefings and news reports.',
    sampleScript: 'Here are the global server metrics reported across our Cloudflare Workers edge nodes.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-arnold.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-sam',
    name: 'Sam',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Raspy',
    ageGroup: 'adult',
    styleCategory: 'energetic',
    description: 'Dynamic, gritty, and charismatic voice tailored for video game characters and promos.',
    sampleScript: 'Push the limits of what browser audio can accomplish without server overhead.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-sam.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-glinda',
    name: 'Glinda',
    provider: 'elevenlabs',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Warm',
    ageGroup: 'senior',
    styleCategory: 'empathetic',
    description: 'Warm, matriarchal, and soothing delivery for bedtime stories and heartfelt audio.',
    sampleScript: 'Rest easy knowing your data is encrypted with modern AES-GCM protocols.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-glinda.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-charlie',
    name: 'Charlie',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-AU',
    languageLabel: 'English (AU)',
    accentRegion: 'Australian Casual',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Authentic Australian accent, warm and approachable for travel and lifestyle media.',
    sampleScript: 'Gday! Ready to explore how fast edge computing really is down under?',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-charlie.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-george',
    name: 'George',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-GB',
    languageLabel: 'English (UK)',
    accentRegion: 'British RP',
    ageGroup: 'senior',
    styleCategory: 'authoritative',
    description: 'Refined BBC-style received pronunciation for history, literature, and luxury brands.',
    sampleScript: 'An exemplary manifestation of architectural elegance and distributed systems engineering.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-george.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-callum',
    name: 'Callum',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'Transatlantic',
    ageGroup: 'adult',
    styleCategory: 'news',
    description: 'Intense, cinematic, and dramatic delivery tailored for thrillers and tech exposés.',
    sampleScript: 'Zero egress costs mean total freedom for media distribution at global scale.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-callum.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },
  {
    id: 'eleven-liam',
    name: 'Liam',
    provider: 'elevenlabs',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Youth',
    ageGroup: 'youth',
    styleCategory: 'energetic',
    description: 'High energy, youthful gaming and streaming persona with crisp articulation.',
    sampleScript: 'Real-time speech synthesis at sub-two-hundred millisecond latency is a game changer!',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-liam.mp3'),
    creditRatePer100Chars: 1.5,
    isCloned: false,
    isFavorite: false,
    modelIdentifier: 'eleven_multilingual_v2',
  },

  // --- Google Cloud TTS (11 voices) ---
  {
    id: 'google-vi-neural2-a',
    name: 'Hoài An (Neural2)',
    provider: 'google',
    gender: 'female',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Miền Bắc (Hà Nội)',
    ageGroup: 'adult',
    styleCategory: 'news',
    description: 'Giọng đọc nữ miền Bắc chuẩn phát thanh truyền hình, trong trẻo, lưu loát và tự nhiên.',
    sampleScript: 'Chào mừng quý khách đến với nền tảng trí tuệ nhân tạo Sofia Platform.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-vi-neural2-a.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Neural2',
    modelIdentifier: 'vi-VN-Neural2-A',
  },
  {
    id: 'google-vi-neural2-d',
    name: 'Quang Minh (Neural2)',
    provider: 'google',
    gender: 'male',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Miền Bắc (Hà Nội)',
    ageGroup: 'adult',
    styleCategory: 'authoritative',
    description: 'Giọng nam miền Bắc trầm ấm, đĩnh đạc, mang phong thái thời sự tin tức và tài liệu chuyên môn.',
    sampleScript: 'Hạ tầng mạng biên Cloudflare Workers bảo đảm độ sẵn sàng liên tục với độ trễ thấp.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-vi-neural2-d.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Neural2',
    modelIdentifier: 'vi-VN-Neural2-D',
  },
  {
    id: 'google-vi-wavenet-a',
    name: 'Bảo Ngọc (Wavenet)',
    provider: 'google',
    gender: 'female',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Miền Nam (Sài Gòn)',
    ageGroup: 'youth',
    styleCategory: 'conversational',
    description: 'Giọng nữ miền Nam ngọt ngào, gần gũi, thích hợp cho quảng cáo, trợ lý ảo và dịch vụ khách hàng.',
    sampleScript: 'Dạ xin chào anh chị! Em có thể hỗ trợ anh chị thiết lập giọng đọc AI hôm nay nha.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-vi-wavenet-a.mp3'),
    creditRatePer100Chars: 0.8,
    isCloned: false,
    isFavorite: false,
    badge: 'Wavenet',
    modelIdentifier: 'vi-VN-Wavenet-A',
  },
  {
    id: 'google-en-journey-f',
    name: 'Journey F',
    provider: 'google',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Expressive',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Google next-gen Journey voice with breath marks, realistic pauses, and human inflection.',
    sampleScript: 'Honestly, hearing how natural these speech inflections are is genuinely fascinating.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-en-journey-f.mp3'),
    creditRatePer100Chars: 1.2,
    isCloned: false,
    isFavorite: true,
    badge: 'Journey',
    modelIdentifier: 'en-US-Journey-F',
  },
  {
    id: 'google-en-journey-d',
    name: 'Journey D',
    provider: 'google',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Conversational',
    ageGroup: 'adult',
    styleCategory: 'storytelling',
    description: 'Smooth and introspective Journey male voice with rich micro-intonations.',
    sampleScript: 'When building scalable software, the simplest solutions often prove to be the most resilient.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-en-journey-d.mp3'),
    creditRatePer100Chars: 1.2,
    isCloned: false,
    isFavorite: false,
    badge: 'Journey',
    modelIdentifier: 'en-US-Journey-D',
  },
  {
    id: 'google-en-neural2-a',
    name: 'Neural2 A',
    provider: 'google',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Professional',
    ageGroup: 'adult',
    styleCategory: 'news',
    description: 'Crisp, high-definition studio delivery designed for e-learning and corporate training.',
    sampleScript: 'All database queries are routed automatically to the closest Cloudflare D1 replica.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-en-neural2-a.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural2',
    modelIdentifier: 'en-US-Neural2-A',
  },
  {
    id: 'google-en-neural2-c',
    name: 'Neural2 C',
    provider: 'google',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Standard',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Balanced, pleasant, and accessible male narration for technical software documentation.',
    sampleScript: 'Our client-side DAW enables smart audio ducking with zero FFmpeg server requirements.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-en-neural2-c.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural2',
    modelIdentifier: 'en-US-Neural2-C',
  },
  {
    id: 'google-en-studio-o',
    name: 'Studio O',
    provider: 'google',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'Broadcast Quality',
    ageGroup: 'adult',
    styleCategory: 'authoritative',
    description: 'Google premium Studio HD voice with unmatched clarity and high acoustic dynamic range.',
    sampleScript: 'Broadcasting live from our global edge infrastructure across over three hundred edge POPs.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-en-studio-o.mp3'),
    creditRatePer100Chars: 1.8,
    isCloned: false,
    isFavorite: false,
    badge: 'Studio HD',
    modelIdentifier: 'en-US-Studio-O',
  },
  {
    id: 'google-ja-neural2-b',
    name: 'Sakura (Neural2)',
    provider: 'google',
    gender: 'female',
    language: 'ja-JP',
    languageLabel: '日本語',
    accentRegion: 'Tokyo Standard',
    ageGroup: 'youth',
    styleCategory: 'warm',
    description: 'Polite, melodic, and crystal-clear Japanese neural voice for mobile apps and anime media.',
    sampleScript: 'ソフィアプラットフォームへようこそ。世界最先端のエッジAIテクノロジーをご体験ください。',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-ja-neural2-b.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural2',
    modelIdentifier: 'ja-JP-Neural2-B',
  },
  {
    id: 'google-fr-neural2-a',
    name: 'Amélie (Neural2)',
    provider: 'google',
    gender: 'female',
    language: 'fr-FR',
    languageLabel: 'Français',
    accentRegion: 'Parisian French',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Elegant, modern Parisian delivery suitable for luxury advertising and podcasts.',
    sampleScript: 'Découvrez la puissance du calcul distribué sans compromis sur la confidentialité.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-fr-neural2-a.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural2',
    modelIdentifier: 'fr-FR-Neural2-A',
  },
  {
    id: 'google-de-neural2-b',
    name: 'Maximilian (Neural2)',
    provider: 'google',
    gender: 'male',
    language: 'de-DE',
    languageLabel: 'Deutsch',
    accentRegion: 'Standard German',
    ageGroup: 'adult',
    styleCategory: 'authoritative',
    description: 'Precise, articulate, and trustworthy German voice for engineering and enterprise portals.',
    sampleScript: 'Hochmoderne Cloud-Architekturen gewährleisten Zuverlässigkeit und höchste Datensicherheit.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/google-de-neural2-b.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural2',
    modelIdentifier: 'de-DE-Neural2-B',
  },

  // --- Microsoft Azure Speech (8 voices) ---
  {
    id: 'azure-vi-hoaimy',
    name: 'Hoài My (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Miền Bắc (Hà Nội)',
    ageGroup: 'youth',
    styleCategory: 'warm',
    description: 'Giọng đọc nữ miền Bắc biểu cảm phong phú, truyền cảm và tự nhiên với công nghệ Azure Neural.',
    sampleScript: 'Công nghệ giọng nói nhân tạo mang đến trải nghiệm giao tiếp tương tác sống động.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-vi-hoaimy.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Expressive',
    modelIdentifier: 'vi-VN-HoaiMyNeural',
  },
  {
    id: 'azure-vi-namminh',
    name: 'Nam Minh (Neural)',
    provider: 'azure',
    gender: 'male',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Miền Nam (Sài Gòn)',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Giọng nam miền Nam hào sảng, phóng khoáng và thân thiện, lý tưởng cho video giải trí và giáo dục.',
    sampleScript: 'Cùng khám phá phòng thu âm thanh kỹ thuật số đa kênh ngay trên trình duyệt web nhé.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-vi-namminh.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'vi-VN-NamMinhNeural',
  },
  {
    id: 'azure-en-jenny',
    name: 'Jenny (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Natural',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Industry-standard conversational female neural voice supporting multi-style emotional presets.',
    sampleScript: 'I can assist you with interactive customer dialogues and intelligent conversational flows.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-en-jenny.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: true,
    badge: 'Multi-Style',
    modelIdentifier: 'en-US-JennyNeural',
  },
  {
    id: 'azure-en-guy',
    name: 'Guy (Neural)',
    provider: 'azure',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Passionate',
    ageGroup: 'adult',
    styleCategory: 'energetic',
    description: 'Passionate and compelling storytelling tone, widely utilized in corporate keynotes.',
    sampleScript: 'When innovation meets distributed execution, scalable applications thrive without limits.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-en-guy.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'en-US-GuyNeural',
  },
  {
    id: 'azure-en-aria',
    name: 'Aria (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'American Broadcast',
    ageGroup: 'adult',
    styleCategory: 'news',
    description: 'Sophisticated, newsreader-grade female voice designed for serious journalism and finance reports.',
    sampleScript: 'Financial transactions are settled atomically via Cloudflare D1 batch commitments.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-en-aria.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'en-US-AriaNeural',
  },
  {
    id: 'azure-zh-xiaoxiao',
    name: 'Xiaoxiao (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'zh-CN',
    languageLabel: '中文 (Mandarin)',
    accentRegion: 'Standard Mandarin',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Natural, cheerful, and popular Mandarin voice for smart speakers and video narrations.',
    sampleScript: '欢迎使用索菲亚人工智能平台，开启您的全方位多模态创作之旅。',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-zh-xiaoxiao.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'zh-CN-XiaoxiaoNeural',
  },
  {
    id: 'azure-ja-nanami',
    name: 'Nanami (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'ja-JP',
    languageLabel: '日本語',
    accentRegion: 'Tokyo Cheerful',
    ageGroup: 'youth',
    styleCategory: 'energetic',
    description: 'Upbeat and friendly Japanese voice popular in conversational assistants and games.',
    sampleScript: 'こんにちは！今日も素晴らしい一日になりますよう、お手伝いいたします。',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-ja-nanami.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'ja-JP-NanamiNeural',
  },
  {
    id: 'azure-fr-denise',
    name: 'Denise (Neural)',
    provider: 'azure',
    gender: 'female',
    language: 'fr-FR',
    languageLabel: 'Français',
    accentRegion: 'French Classical',
    ageGroup: 'adult',
    styleCategory: 'storytelling',
    description: 'Warm and expressive narration style for literary readings and promotional media.',
    sampleScript: 'Chaque mot prononcé porte la nuance et la richesse de notre moteur vocal distribué.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/azure-fr-denise.mp3'),
    creditRatePer100Chars: 1.0,
    isCloned: false,
    isFavorite: false,
    badge: 'Neural',
    modelIdentifier: 'fr-FR-DeniseNeural',
  },

  // --- Custom Cloned Voices (3 seed profiles) ---
  {
    id: 'clone-sophia-exec',
    name: 'Sophia Executive Persona',
    provider: 'custom_clone',
    gender: 'female',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt / English',
    accentRegion: 'Bilingual Corporate',
    ageGroup: 'adult',
    styleCategory: 'authoritative',
    description: 'Mẫu giọng đại diện thương hiệu chính thức của Sophia Platform, hỗ trợ song ngữ Anh - Việt mượt mà.',
    sampleScript: 'Sophia Platform mang đến giải pháp AI toàn diện cho doanh nghiệp với tốc độ xử lý mạng biên ấn tượng.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/clone-sophia-exec.mp3'),
    creditRatePer100Chars: 2.0,
    isCloned: true,
    isFavorite: true,
    badge: 'Cloned Official',
  },
  {
    id: 'clone-viet-techlead',
    name: 'Việt Quang TechLead',
    provider: 'custom_clone',
    gender: 'male',
    language: 'vi-VN',
    languageLabel: 'Tiếng Việt',
    accentRegion: 'Hà Nội Technical',
    ageGroup: 'adult',
    styleCategory: 'conversational',
    description: 'Giọng đọc kỹ thuật chuyên sâu, giải thích kiến trúc vi dịch vụ và hệ sinh thái Cloudflare Workers.',
    sampleScript: 'Hôm nay chúng ta sẽ cùng phân tích quy trình streaming thời gian thực qua server-sent events.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/clone-viet-techlead.mp3'),
    creditRatePer100Chars: 2.0,
    isCloned: true,
    isFavorite: false,
    badge: 'Cloned',
  },
  {
    id: 'clone-podcast-host',
    name: 'David Podcast Host',
    provider: 'custom_clone',
    gender: 'male',
    language: 'en-US',
    languageLabel: 'English (US)',
    accentRegion: 'Pacific Northwest',
    ageGroup: 'adult',
    styleCategory: 'warm',
    description: 'Warm, podcast studio microphone quality clone with intimate proximity effect.',
    sampleScript: 'Welcome back to the Deep Tech podcast, broadcasting worldwide from the edge.',
    previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/clone-podcast-host.mp3'),
    creditRatePer100Chars: 2.0,
    isCloned: true,
    isFavorite: false,
    badge: 'Cloned',
  },
];

export const useVoiceCatalogStore = create<VoiceCatalogState>((set, get) => ({
  voices: SEED_VOICES,
  activeAuditionVoiceId: null,
  isPlaying: false,
  playbackProgress: 0,
  playbackSpeed: 1.0,

  activeTab: 'all',
  searchQuery: '',
  providerFilter: 'all',
  languageFilter: 'all',
  genderFilter: 'all',
  styleFilter: 'all',

  setActiveTab: (tab) => set({ activeTab: tab }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setProviderFilter: (provider) => set({ providerFilter: provider }),
  setLanguageFilter: (lang) => set({ languageFilter: lang }),
  setGenderFilter: (gender) => set({ genderFilter: gender }),
  setStyleFilter: (style) => set({ styleFilter: style }),

  resetFilters: () =>
    set({
      searchQuery: '',
      providerFilter: 'all',
      languageFilter: 'all',
      genderFilter: 'all',
      styleFilter: 'all',
    }),

  toggleFavorite: (voiceId) => {
    set((state) => ({
      voices: state.voices.map((v) =>
        v.id === voiceId ? { ...v, isFavorite: !v.isFavorite } : v
      ),
    }));
  },

  playAudition: (voiceId) => {
    const { activeAuditionVoiceId, isPlaying } = get();
    if (activeAuditionVoiceId === voiceId && isPlaying) {
      // Toggle to pause
      set({ isPlaying: false });
      return;
    }

    set({
      activeAuditionVoiceId: voiceId,
      isPlaying: true,
      playbackProgress: 0,
    });
  },

  pauseAudition: () => set({ isPlaying: false }),
  stopAudition: () => set({ isPlaying: false, activeAuditionVoiceId: null, playbackProgress: 0 }),
  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
  setPlaybackProgress: (progress) => set({ playbackProgress: progress }),

  createClonedVoice: (params) => {
    const newVoice: VoiceItem = {
      id: `clone-${Date.now()}`,
      name: params.name.trim(),
      provider: 'custom_clone',
      gender: params.gender || 'neutral',
      language: params.language || 'vi-VN',
      languageLabel: params.language === 'vi-VN' ? 'Tiếng Việt' : 'English',
      accentRegion: params.accentRegion || 'Custom Accent',
      ageGroup: 'adult',
      styleCategory: 'conversational',
      description: params.description.trim() || 'Custom cloned voice profile.',
      sampleScript:
        params.sampleScript ||
        'Giọng đọc AI nhân bản đặc trưng cho không gian làm việc của bạn trên nền tảng Sofia Platform.',
      previewSampleUrl: resolveWorkspaceMediaUrl('audio/samples/custom-clone.mp3'),
      creditRatePer100Chars: 2.0,
      isCloned: true,
      isFavorite: true,
      badge: 'Custom Clone',
    };

    set((state) => ({
      voices: [newVoice, ...state.voices],
      activeTab: 'cloned',
    }));

    return newVoice;
  },

  deleteClonedVoice: (id) => {
    set((state) => ({
      voices: state.voices.filter((v) => v.id !== id),
      activeAuditionVoiceId: state.activeAuditionVoiceId === id ? null : state.activeAuditionVoiceId,
      isPlaying: state.activeAuditionVoiceId === id ? false : state.isPlaying,
    }));
  },

  getFilteredVoices: () => {
    const {
      voices,
      activeTab,
      searchQuery,
      providerFilter,
      languageFilter,
      genderFilter,
      styleFilter,
    } = get();

    const query = searchQuery.trim().toLowerCase();

    return voices.filter((v) => {
      // Tab filter
      if (activeTab === 'cloned' && !v.isCloned) return false;
      if (activeTab === 'favorites' && !v.isFavorite) return false;

      // Provider filter
      if (providerFilter !== 'all' && v.provider !== providerFilter) return false;

      // Language filter
      if (languageFilter !== 'all' && !v.language.startsWith(languageFilter)) return false;

      // Gender filter
      if (genderFilter !== 'all' && v.gender !== genderFilter) return false;

      // Style filter
      if (styleFilter !== 'all' && v.styleCategory !== styleFilter) return false;

      // Search query
      if (query) {
        const normalizeStr = (s: string) =>
          s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd');
        const normQuery = normalizeStr(query);
        const compactQuery = normQuery.replace(/\s+/g, '');

        const checkMatch = (target: string) => {
          const lower = target.toLowerCase();
          const norm = normalizeStr(target);
          const compact = norm.replace(/\s+/g, '');
          return (
            lower.includes(query) ||
            norm.includes(normQuery) ||
            (compactQuery.length >= 3 && compact.includes(compactQuery))
          );
        };

        const matchesName = checkMatch(v.name);
        const matchesDesc = checkMatch(v.description);
        const matchesLang = checkMatch(v.languageLabel);
        const matchesAccent = checkMatch(v.accentRegion);
        const matchesProvider = checkMatch(v.provider);
        return matchesName || matchesDesc || matchesLang || matchesAccent || matchesProvider;
      }

      return true;
    });
  },

  getActiveAuditionVoice: () => {
    const { voices, activeAuditionVoiceId } = get();
    return voices.find((v) => v.id === activeAuditionVoiceId);
  },

  getVoiceStats: () => {
    const { voices } = get();
    return {
      totalVoices: voices.length,
      providerCount: 4,
      clonedCount: voices.filter((v) => v.isCloned).length,
      favoritesCount: voices.filter((v) => v.isFavorite).length,
    };
  },
}));
